/**
 * Storehouses Store
 *
 * Singleton reactive store for the current business's storehouses.
 * All components should read from this store via `getStorehousesList()`
 * instead of calling `getStorehouses()` independently.
 *
 * Call `fetchStorehouses()` once at app init (MainLayout).
 * Call `refreshStorehouses()` after creating/updating/deleting storehouses.
 */

import { createSignal } from 'solid-js';
import { getStorehouses } from '@/shared/api/storehouses.api';
import type { Storehouse } from '@/shared/types/storehouse.types';

const [storehouses, setStorehouses] = createSignal<Storehouse[]>([]);
const [isLoaded, setIsLoaded] = createSignal(false);

/**
 * Fetch storehouses from the API and cache in the store.
 * Safe to call multiple times — only fetches once unless forced.
 */
export async function fetchStorehouses(force = false): Promise<Storehouse[]> {
  if (isLoaded() && !force) return storehouses();
  try {
    const data = await getStorehouses(
      force ? ({ refresh: 'true' } as any) : undefined
    );
    setStorehouses(data);
    return data;
  } catch {
    setStorehouses([]);
    return [];
  } finally {
    setIsLoaded(true);
  }
}

/**
 * Force-refresh storehouses from the API (bypasses backend cache too).
 */
export async function refreshStorehouses(): Promise<Storehouse[]> {
  return fetchStorehouses(true);
}

/**
 * Get the current storehouses list (reactive accessor).
 * Returns [] until fetchStorehouses() completes.
 */
export function getStorehousesList(): Storehouse[] {
  return storehouses();
}

/**
 * Check if storehouse data has been loaded.
 */
export function isStorehousesLoaded(): boolean {
  return isLoaded();
}

/**
 * Get count of locked storehouses (for downgrade banners).
 */
export function getLockedStorehouseCount(): number {
  return storehouses().filter((s) => s.isLocked).length;
}

/**
 * Clear storehouse data from the store (called on logout).
 */
export function clearStorehouses(): void {
  setStorehouses([]);
  setIsLoaded(false);
}
